import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { adminCleanersRoster, type AdminCleaner } from "@/mocks/admin";
import CleanerActionMenu from "./components/CleanerActionMenu";
import CleanerDrawer from "./components/CleanerDrawer";
import ConfirmRemoveModal from "./components/ConfirmRemoveModal";
import AddCleanerModal from "./components/AddCleanerModal";

type StatusFilter = "all" | AdminCleaner["status"];

const statusFilters: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "onboarding", label: "Onboarding" },
  { id: "on_leave", label: "On leave" },
];

const statusPill: Record<AdminCleaner["status"], string> = {
  active: "bg-primary-100 text-primary-700",
  onboarding: "bg-accent-100 text-accent-700",
  on_leave: "bg-background-200 text-foreground-600",
};

const statusLabel: Record<AdminCleaner["status"], string> = {
  active: "Active",
  onboarding: "Onboarding",
  on_leave: "On leave",
};

export default function AdminCleanersPage() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [roster, setRoster] = useState<AdminCleaner[]>(adminCleanersRoster);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roster.filter((c) => {
      if (filter !== "all" && c.status !== filter) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.role.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q)
      );
    });
  }, [search, filter, roster]);

  const counts = useMemo(
    () => ({
      all: roster.length,
      active: roster.filter((c) => c.status === "active").length,
      onboarding: roster.filter((c) => c.status === "onboarding").length,
      on_leave: roster.filter((c) => c.status === "on_leave").length,
    }),
    [roster],
  );

  const active = roster.find((c) => c.id === activeId) || null;
  const removing = roster.find((c) => c.id === removeId) || null;

  const handleStatusChange = (id: string, next: AdminCleaner["status"]) => {
    const target = roster.find((c) => c.id === id);
    setRoster((r) => r.map((c) => (c.id === id ? { ...c, status: next } : c)));
    if (target) {
      setToast(`${target.name.split(" ")[0]} marked as ${statusLabel[next]}`);
    }
  };

  const handleRemove = (id: string) => {
    const target = roster.find((c) => c.id === id);
    setRoster((r) => r.filter((c) => c.id !== id));
    setRemoveId(null);
    if (activeId === id) setActiveId(null);
    if (target) setToast(`${target.name} removed from roster`);
  };

  return (
    <AdminLayout
      title="Cleaners"
      subtitle={`${roster.length} on roster`}
      action={
        <button
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Add cleaner
        </button>
      }
    >
      <div className="space-y-4">
        {/* Filter row */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, role or city"
              className="w-full bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-500 rounded-md pl-9 pr-3 py-2 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 overflow-x-auto">
            {statusFilters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  filter === f.id
                    ? "bg-background-50 text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {f.label}
                <span className="ml-1.5 text-foreground-500">
                  {counts[f.id as keyof typeof counts]}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-3">
                <i className="ri-user-search-line text-foreground-500 text-xl"></i>
              </span>
              <p className="text-sm font-medium text-foreground-800">No cleaners match</p>
              <p className="text-xs text-foreground-600 mt-1">
                Try a different search or filter
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-background-200">
              {filtered.map((c) => (
                <li
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3.5 hover:bg-background-100/60 transition-colors cursor-pointer"
                >
                  <img
                    src={c.image}
                    alt={c.name}
                    className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover object-top flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-foreground-950 truncate">
                        {c.name}
                      </p>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusPill[c.status]}`}
                      >
                        {statusLabel[c.status]}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-600 truncate">
                      {c.role} · {c.city}
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center gap-5 text-xs text-foreground-700">
                    <div className="text-right">
                      <p className="font-semibold text-foreground-950">{c.jobs}</p>
                      <p className="text-[10px] text-foreground-500">jobs</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-foreground-950 flex items-center justify-end gap-1">
                        {c.rating > 0 ? (
                          <>
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                            {c.rating.toFixed(2)}
                          </>
                        ) : (
                          <span className="text-foreground-500">—</span>
                        )}
                      </p>
                      <p className="text-[10px] text-foreground-500">rating</p>
                    </div>
                  </div>
                  <CleanerActionMenu
                    cleaner={c}
                    onView={() => setActiveId(c.id)}
                    onStatusChange={(next) => handleStatusChange(c.id, next)}
                    onRemove={() => setRemoveId(c.id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <CleanerDrawer
        cleaner={active}
        onClose={() => setActiveId(null)}
        onStatusChange={(next) => active && handleStatusChange(active.id, next)}
        onRemove={() => active && setRemoveId(active.id)}
      />

      <AddCleanerModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={(c) => {
          setRoster((r) => [c, ...r]);
          setToast(`${c.name} added to roster`);
        }}
      />

      <ConfirmRemoveModal
        cleaner={removing}
        onCancel={() => setRemoveId(null)}
        onConfirm={() => removing && handleRemove(removing.id)}
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] bg-foreground-950 text-background-50 text-xs px-4 py-2.5 rounded-full shadow animate-fade-up flex items-center gap-2">
          <i className="ri-check-line"></i>
          {toast}
        </div>
      )}
    </AdminLayout>
  );
}